/* =========================================================================
 * Achievements — homepage section.
 *
 * Curated subset of the 33 in-app badges (defined in the Android app's
 * Achievements.kt). Shown as a 4-column grid; emoji + title + short copy.
 *
 * Goal per brief: the system should feel meaningful, not financial.
 * Hence the emphasis on contribution / recognition / community language.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Subset chosen to span the breadth of achievement types: getting-started,
// sensor-specific, geographic, ranking, mode-based, meta. Twelve cards
// fit cleanly as 4×3 on desktop, 2×6 on mobile.
const BADGES = [
  { emoji: "🌱", title: "First steps", body: "Your first reading shared." },
  { emoji: "💯", title: "Getting started", body: "100 readings contributed." },
  { emoji: "🎯", title: "Regular", body: "1,000 readings — you're a real contributor." },
  { emoji: "🦸", title: "Bripplex hero", body: "100,000 readings — community-shaping." },
  { emoji: "🌡️", title: "Weather watcher", body: "1,000 barometer readings." },
  { emoji: "🧭", title: "Navigator", body: "1,000 location points contributed." },
  { emoji: "🏆", title: "Completionist", body: "Every sensor used at least once." },
  { emoji: "🏃", title: "10 km", body: "Walked, ran, or rode 10 km contributing." },
  { emoji: "🗺️", title: "Local hero", body: "Top 10 in any geographic area." },
  { emoji: "🥇", title: "Hall of Fame", body: "Reached the global top 10." },
  { emoji: "🚗", title: "Road tripper", body: "1 hour logged in Driving mode." },
  { emoji: "🌟", title: "I did it all", body: "1+ hour in every activity mode." },
];

export function Achievements() {
  return (
    <MKBox
      component="section"
      id="achievements"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[200]}` })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Contribution & achievements
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              Recognition for the people who power it
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 640, mx: "auto" }}>
              33 milestones across sensor activity, distance, sustained sessions, geographic
              coverage, and community ranking. Meaningful goals, not gamified for money.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          {BADGES.map((b) => (
            <Grid item xs={6} sm={4} md={3} key={b.title}>
              <Card
                elevation={0}
                sx={({ palette, borders }) => ({
                  p: 2.5,
                  height: "100%",
                  textAlign: "center",
                  border: `1px solid ${palette.grey[200]}`,
                  borderRadius: borders.borderRadius.lg,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 14px 28px -16px rgba(15, 23, 42, 0.18)",
                    borderColor: palette.primary.main,
                  },
                })}
              >
                <MKBox sx={{ fontSize: 36, lineHeight: 1, mb: 1 }}>{b.emoji}</MKBox>
                <MKTypography
                  variant="subtitle2"
                  color="dark"
                  fontWeight="bold"
                  sx={{ letterSpacing: "-0.005em" }}
                >
                  {b.title}
                </MKTypography>
                <MKTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                  {b.body}
                </MKTypography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <MKTypography
          variant="caption"
          color="text"
          sx={{ display: "block", textAlign: "center", mt: 4, opacity: 0.7 }}
        >
          + 21 more in the app — including session modes, ambient-condition tiers, and per-area
          champions.
        </MKTypography>
      </Container>
    </MKBox>
  );
}
